import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, Typography, Button, Avatar } from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import LockIcon from '@mui/icons-material/Lock';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { uploadAvatar, changePass } from '../../services/user-services';
import { toast } from 'react-toastify';
import { CssTextField } from '../layout/elements';

/* ------------------------------------------------------------------ *
 * BWF · Account — "Match Card" styling. Same logic: avatar resize +
 * upload, change password, logout. Only the presentation changed.
 * ------------------------------------------------------------------ */

const ORANGE = '#ff9800';
const RED = '#ef5350';
const LINE = '1px solid rgba(255,255,255,0.08)';

const initials = (u) => (u?.username ? u.username.slice(0, 2).toUpperCase() : '??');
const avatarUrl = (p) => {
  if (!p) return null;
  return p.startsWith('http') ? p : `${process.env.REACT_APP_API_URL_BASE}${p}`;
};

const Card = styled(Box)({ background: '#1e1e1e', border: LINE, borderRadius: '16px', padding: 18 });

const FieldLabel = styled(Typography)({
  fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: '#b0b0b0',
});

// Password field: orange CssTextField, left-aligned, lock adornment.
const PassField = styled(CssTextField)({
  width: '100%',
  '& .MuiInputBase-input': { textAlign: 'left', color: '#fff', fontSize: '1rem' },
  '& .MuiOutlinedInput-root': { borderRadius: 11, background: '#161616' },
});

function Account() {
  const { authData, setAuth } = useAuth();
  const navigate = useNavigate();
  const [image, setImage] = useState();
  const [oldPassword, setOldPassword] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');

  const passMatch = () => password === password2;

  const processImage = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const size = 64;
          canvas.width = size; canvas.height = size;
          const ctx = canvas.getContext('2d');
          let sx, sy, sw, sh;
          if (img.width > img.height) { sw = img.height; sh = img.height; sx = (img.width - img.height) / 2; sy = 0; }
          else { sw = img.width; sh = img.width; sx = 0; sy = (img.height - img.width) / 2; }
          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, size, size);
          canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.8);
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });

  const uploadFile = async (e) => {
    e.preventDefault();
    if (!image) return;
    try {
      const resized = await processImage(image);
      const uploadData = new FormData();
      uploadData.append('image', resized, image.name);
      const uploaded = await uploadAvatar(authData.user.profile?.id, uploadData);
      if (uploaded) {
        toast.success('Image uploaded and resized');
        const newUser = { ...authData };
        newUser.user.profile = uploaded;
        setAuth(newUser);
        setImage(null);
      } else {
        toast.error('Error. Image was not uploaded');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error processing image');
    }
  };

  const submitChangePass = async (e) => {
    e.preventDefault();
    if (passMatch()) {
      const passData = await changePass(
        { old_password: oldPassword, new_password: password },
        authData.user.id,
        authData.token,
      );
      if (passData) toast.success('Password has been changed');
    } else {
      toast.error("Passwords don't match");
    }
  };

  const logout = () => { setAuth(null); navigate('/login'); };

  if (!authData) return null;
  const user = authData.user;

  return (
    <Box sx={{ textAlign: 'left', pb: 4 }}>
      <Box sx={{ m: '10px 2px 20px' }}>
        <Typography sx={{ fontSize: 13, color: '#b0b0b0', fontWeight: 500 }}>Settings</Typography>
        <Typography sx={{ fontSize: 26, fontWeight: 800, color: '#fff', fontFamily: '"Archivo", Inter, sans-serif', letterSpacing: '-0.3px' }}>
          Your account
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        {/* profile header */}
        <Card>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Avatar src={avatarUrl(user.profile?.image)} sx={{ width: 56, height: 56, fontSize: 18, fontWeight: 700, bgcolor: 'rgba(255,255,255,0.12)' }}>
              {initials(user)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>{user.username}</Typography>
              {user.email && <Typography sx={{ fontSize: 13, color: '#b0b0b0', mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</Typography>}
            </Box>
            <Button onClick={logout} variant="outlined" sx={{
              color: RED, borderColor: 'rgba(239,83,80,0.4)', fontWeight: 600, borderRadius: '9px', textTransform: 'none',
              '&:hover': { borderColor: RED, background: 'rgba(239,83,80,0.08)' },
            }}>Logout</Button>
          </Box>
        </Card>

        {/* avatar upload */}
        <Card>
          <FieldLabel>Profile photo</FieldLabel>
          <Box component="form" onSubmit={uploadFile} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5 }}>
            <Avatar src={avatarUrl(user.profile?.image)} sx={{ width: 48, height: 48, fontSize: 15, fontWeight: 700, bgcolor: 'rgba(255,255,255,0.12)' }}>
              {initials(user)}
            </Avatar>
            <Button component="label" sx={{
              flex: 1, height: 50, borderRadius: '11px', border: `1.5px dashed ${image ? 'rgba(255,152,0,0.5)' : 'rgba(255,255,255,0.14)'}`,
              color: image ? '#fff' : '#b0b0b0', textTransform: 'none', fontSize: 13, fontWeight: 500, gap: 1,
            }}>
              <FileUploadOutlinedIcon sx={{ fontSize: 18 }} />
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {image ? image.name : 'Choose image'}
              </Box>
              <input type="file" accept="image/*" hidden onChange={(e) => setImage(e.target.files[0])} />
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={!image} sx={{ borderRadius: '10px', py: 1.5 }}>Upload</Button>
          </Box>
        </Card>

        {/* security */}
        <Card>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ShieldIcon sx={{ fontSize: 19, color: ORANGE }} />
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>Security</Typography>
          </Box>
          <Box component="form" onSubmit={submitChangePass} sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.875 }}>
              <FieldLabel>Current password</FieldLabel>
              <PassField type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)}
                InputProps={{ startAdornment: <LockIcon sx={{ mr: 1, fontSize: 18, color: '#7a7a7a' }} /> }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.875 }}>
              <FieldLabel>New password</FieldLabel>
              <PassField type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                InputProps={{ startAdornment: <LockIcon sx={{ mr: 1, fontSize: 18, color: '#7a7a7a' }} /> }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.875 }}>
              <FieldLabel>Repeat new password</FieldLabel>
              <PassField type="password" value={password2} onChange={(e) => setPassword2(e.target.value)}
                error={password2.length > 0 && !passMatch()}
                helperText={password2.length > 0 && !passMatch() ? "Passwords don't match" : ''}
                InputProps={{ startAdornment: <LockIcon sx={{ mr: 1, fontSize: 18, color: '#7a7a7a' }} /> }} />
            </Box>
            <Button type="submit" variant="contained" color="primary"
              disabled={!oldPassword || !password || !passMatch()}
              sx={{ mt: 0.5, py: 1.4, fontSize: 15, fontWeight: 700, borderRadius: '12px' }}>
              Update password
            </Button>
          </Box>
        </Card>
      </Box>
    </Box>
  );
}

export default Account;
